/**
 * Permit Wall - DISPLAY-only office TV page (separate from the standard
 * card-grid dashboard, which the other roles keep unchanged).
 *
 * Two zones over a live poll of the active transactions:
 *   • Top: the fixed five most-recently-updated transactions as cards.
 *   • Bottom: a GPU-composited right-to-left ticker of the transactions
 *     changed inside the recent working-day window (excluding the five up
 *     top).
 * Three event types (موافقة / عدم موافقة / تكليف) flash the newest card
 * for ~8 s and play a distinct sound once; status pills are static color.
 *
 * The page renders its own fixed full-viewport shell with a bespoke
 * per-theme palette applied as CSS variables, so it does not use the app
 * Header/AppLayout chrome. It is intentionally non-interactive beyond the
 * theme toggle and the one-time audio-unlock tap.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listTransactionsWithActions } from "@/api/transactions";
import { useThemeStore } from "@/stores/themeStore";
import { deriveWall, WALL_SURFACE, type WallTheme } from "@/lib/wall";
import { useWallArrivals } from "@/hooks/useWallArrivals";
import { WallCard } from "@/components/wall/WallCard";
import { WallTicker } from "@/components/wall/WallTicker";
import { WallClock } from "@/components/wall/WallClock";
import { SoundUnlockOverlay } from "@/components/wall/SoundUnlockOverlay";

const POLL_INTERVAL_MS = 30_000;
const TOP_COUNT = 5;
const WINDOW_DAYS = 3;
const CURSOR_IDLE_MS = 5000;

/** Mirrors the app's login.officeName / login.subtitle. Hardcoded here
 *  rather than via i18n because the wall is a single-locale (Arabic)
 *  display and must not mutate the shared document locale. */
const OFFICE_NAME = "مكتب المهندس عزام المغاريز للاستشارات الهندسية";
const OFFICE_SUBTITLE = "لوحة متابعة معاملات أمانة عمّان الكبرى";

export function WallPage() {
  const theme = useThemeStore((s) => s.theme) as WallTheme;
  const toggleTheme = useThemeStore((s) => s.toggle);

  const query = useQuery({
    queryKey: ["transactions", "wall"],
    queryFn: () => listTransactionsWithActions({ status: "ACTIVE", limit: 200 }),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 0,
  });

  // Recompute the two zones only when the underlying data changes. `now`
  // is captured per poll, which is fine: the working-day window only needs
  // day-level precision and polls land every 30 s.
  const { topRows, tickerRows } = useMemo(
    () => deriveWall(query.data?.items ?? [], { topCount: TOP_COUNT, windowDays: WINDOW_DAYS }),
    [query.data],
  );

  const { flashId, flashEvent } = useWallArrivals(topRows, query.isSuccess);

  // Cursor visible by default; hide after 5 s idle, restore on movement.
  useEffect(() => {
    let timer: number | undefined;
    const onMove = () => {
      document.documentElement.style.cursor = "";
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        document.documentElement.style.cursor = "none";
      }, CURSOR_IDLE_MS);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    onMove();
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.clearTimeout(timer);
      document.documentElement.style.cursor = "";
    };
  }, []);

  const surfaceVars = WALL_SURFACE[theme] as React.CSSProperties;
  // LTR toggle: knob sits left in light, right in dark (matches the icons).
  const knobX = theme === "light" ? "0.18vw" : "2.55vw";

  return (
    <div
      dir="rtl"
      lang="ar"
      style={{
        ...surfaceVars,
        position: "fixed",
        inset: 0,
        background: "var(--page-bg)",
        fontFamily: "'Tajawal', sans-serif",
        color: "var(--text)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      <SoundUnlockOverlay />

      {/* Top bar: office identity, live indicator, theme toggle, clock. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.9vh 2.6vw 1.1vh",
          flex: "none",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4vh" }}>
          <div style={{ fontSize: "2vw", fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
            {OFFICE_NAME}
          </div>
          <div style={{ fontSize: "1.02vw", fontWeight: 600, color: "var(--text-dim)", letterSpacing: "0.01em" }}>
            {OFFICE_SUBTITLE}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "1.5vw" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.55vw", fontSize: "1.02vw", fontWeight: 700, color: "var(--text-dim)" }}>
            <span
              style={{
                width: "0.7vw",
                height: "0.7vw",
                borderRadius: "50%",
                background: "#43d98a",
                boxShadow: "0 0 14px #43d98a",
                animation: "wallLiveDot 1.8s ease-in-out infinite",
              }}
            />
            مباشر
          </div>

          <button
            type="button"
            dir="ltr"
            onClick={toggleTheme}
            title="تبديل المظهر"
            aria-label="تبديل المظهر"
            style={{
              position: "relative",
              width: "4.8vw",
              height: "2.4vw",
              borderRadius: "2vw",
              background: "var(--toggle-track)",
              border: "1px solid var(--toggle-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 0.55vw",
              cursor: "pointer",
              flex: "none",
              boxSizing: "border-box",
            }}
          >
            <svg viewBox="0 0 24 24" style={{ width: "1.15vw", height: "1.15vw", position: "relative", zIndex: 1 }} fill="none" stroke="var(--toggle-icon)" strokeWidth={2} strokeLinecap="round" aria-hidden>
              <circle cx="12" cy="12" r="4.5" />
              <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5 5l1.4 1.4M17.6 17.6L19 19M19 5l-1.4 1.4M6.4 17.6L5 19" />
            </svg>
            <svg viewBox="0 0 24 24" style={{ width: "1.05vw", height: "1.05vw", position: "relative", zIndex: 1 }} fill="var(--toggle-icon)" stroke="none" aria-hidden>
              <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
            </svg>
            <span
              aria-hidden
              style={{
                position: "absolute",
                top: "50%",
                left: 0,
                width: "2vw",
                height: "2vw",
                marginTop: "-1vw",
                borderRadius: "50%",
                background: "var(--toggle-knob)",
                boxShadow: "0 0.2vh 0.7vh rgba(0,0,0,0.28)",
                transform: `translateX(${knobX})`,
                transition: "transform .35s cubic-bezier(.4,0,.2,1)",
              }}
            />
          </button>

          <WallClock
            style={{ fontSize: "2.2vw", fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em", color: "var(--text)" }}
          />
        </div>
      </div>

      {/* Top zone: the fixed five most-recently-updated cards. */}
      <div style={{ flex: 1, display: "flex", gap: "1.3vw", padding: "0.8vh 2.6vw 1.6vh", minHeight: 0 }}>
        {topRows.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-dim)", fontSize: "1.6vw", fontWeight: 700 }}>
            {query.isError ? "تعذّر تحميل البيانات" : query.isPending ? "جارٍ التحميل…" : "لا توجد معاملات نشطة"}
          </div>
        ) : (
          topRows.map((row) => (
            <WallCard key={row.id} row={row} theme={theme} flashEvent={flashId === row.id ? flashEvent : null} />
          ))
        )}
      </div>

      <WallTicker rows={tickerRows} theme={theme} windowDays={WINDOW_DAYS} />
    </div>
  );
}
